import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FilePreviewProvider, FilePreview, useFilePreview } from '@/common/components/FilePreview';
import { PasseriniAPI } from '@/services/apiService';
import { Loader2 } from 'lucide-react';
import type { DniDocumentoRecord } from '../types';
import toast from 'react-hot-toast';

interface DniPreviewDialogProps {
  documento: DniDocumentoRecord | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
}

export const DniPreviewDialog: React.FC<DniPreviewDialogProps> = ({
  documento,
  open,
  onOpenChange,
  title,
}) => {
  if (!documento) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl h-[90dvh] flex flex-col p-0">
        <DialogHeader className="py-4 pl-6 pr-12 border-b">
          <DialogTitle className="truncate" title={title}>
            {title}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Vista previa del documento de identidad {title}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden">
          <FilePreviewProvider>
            <DniPreviewContent documento={documento} open={open} />
          </FilePreviewProvider>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const DniPreviewContent: React.FC<{ documento: DniDocumentoRecord; open: boolean }> = ({
  documento,
  open,
}) => {
  const { open: openPreview } = useFilePreview();
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let isMounted = true;

    const fetchAndPreview = async () => {
      if (!open || !documento.fileUrl) return;

      setIsLoading(true);
      setError(null);

      try {
        const response = await PasseriniAPI.get(documento.fileUrl.replace(/^\/api/, ''), {
          responseType: 'blob',
        });

        if (!isMounted) return;

        const blobUrl = URL.createObjectURL(response.data);
        openPreview(blobUrl, documento.fileName);
      } catch (err) {
        console.error('Error fetching DNI for preview:', err);
        if (isMounted) {
          setError('Error al cargar el DNI para previsualización');
          toast.error('Error al cargar el DNI');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchAndPreview();

    return () => {
      isMounted = false;
    };
  }, [documento.fileName, documento.fileUrl, open, openPreview]);

  if (isLoading) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-gray-400" />
          <p className="text-sm text-gray-500 mt-2">Cargando DNI...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="text-center">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  return <FilePreview className="w-full h-full" />;
};
